import { useMemo, useState } from 'react';
// Helpers
import clsx from 'clsx';
import { colors } from 'design-system';
// Models
import {
  CoverageCatalog,
  CoverageCatalogResume,
  Quotation,
  QuotationCoverage,
  QuotationUnit,
} from 'data-layer-cr/dist/models';
import { LUC } from 'src/constants/coveragesCodes';
// Hooks
import { useCoverageData } from 'src/hooks/useCoverageData';
// Components
import { faShieldCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import { capitalize } from 'lodash';
import CoverageDetailRow from './CoverageDetailRow';
import { ExemptionCoverage, ExemptionCoverageId } from './ExemptionCoverage';
import LUCCoverage from './LUCCoverage';
import PersonalizationModal from './PersonalizationModal';
import { CustomButtonColor, useStyle } from './styles';

type Props = {
  quotation: Quotation;
  unit: QuotationUnit;
  coverageCatalogs: CoverageCatalog[];
  coverageCatalogResumes: CoverageCatalogResume[];
  anualPrice: number;
  shouldPersonalize?: boolean;
  onChangeCoverageLimit: (
    modifiedPackage: number,
    modifiedCoverage: QuotationCoverage[],
    shouldUpdate: boolean,
    packageSelected: string,
  ) => void;
  packageOption: number;
  savedQuotationUnit: QuotationUnit[];
  onChangeExemptionCoverage: (
    modifiedPackage: number,
    addExemptionCoverage: boolean,
  ) => void;
  currentSelectedPlan: string;
  onChangeLoadingPackage?: () => void;
};

const DEFAULT_COLORS: CustomButtonColor = {
  default: colors.bright_turquoise,
  hover: colors.bright_turquoise,
  active: colors.madison,
  background: '',
};

const IndividualCoveragesCard = ({
  quotation,
  unit,
  coverageCatalogs,
  coverageCatalogResumes,
  shouldPersonalize,
  anualPrice,
  packageOption,
  savedQuotationUnit,
  onChangeCoverageLimit,
  onChangeExemptionCoverage,
  currentSelectedPlan,
  onChangeLoadingPackage,
}: Props): JSX.Element => {
  const classes = useStyle(DEFAULT_COLORS);
  const [openModal, setOpenModal] = useState(false);
  const moneyType = useMemo(
    () => quotation.quotationProfile.typeOfMoney,
    [quotation],
  );

  const lucOption =
    unit.coverages.filter((x) => x.coverageId === LUC).length > 0;

  const { coveragesResume, customizableCoverages } = useCoverageData({
    coverageCatalogResumes,
    coverageCatalogs,
    unit,
    moneyType,
    LUCOption: lucOption,
    createdAt: quotation.createdAt,
  });

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  return (
    <>
      <Box data-testid="individual-coverages-card">
        <Grid
          container
          className={classes.titleSection}
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center" style={{ gap: '0.5rem' }}>
            <FontAwesomeIcon icon={faShieldCheck} size="2xl" color="#0D2E68" />
            <Typography className={classes.titleText} data-testid="title-text">
              Coberturas
            </Typography>
          </Box>

          {Boolean(shouldPersonalize) && (
            <Button
              className={clsx(classes.baseButton, classes.outlinedButton)}
              style={{ marginLeft: '20px' }}
              size="small"
              data-testid="personilize-button"
              type="button"
              onClick={handleOpenModal}
            >
              Personalizar
            </Button>
          )}
        </Grid>
        <Grid>
          <LUCCoverage
            data-testid="LUCOption-CheckBox"
            packageOption={packageOption}
            unit={unit}
            savedQuotationUnit={savedQuotationUnit}
            currentSelectedPlan={currentSelectedPlan}
            onChangeLoadingPackage={() => onChangeLoadingPackage?.()}
          />
        </Grid>
        <Box
          display="flex"
          flexDirection="column"
          marginTop="1rem"
          style={{ gap: '1rem' }}
        >
          {coveragesResume
            .filter((item) => item.code !== ExemptionCoverageId)
            .map((coverage) => (
              <CoverageDetailRow
                key={coverage.code}
                data-testid={`coverages-${coverage.code}`}
                name={capitalize(coverage.name)}
                code={coverage.code}
                value={coverage.value}
              />
            ))}
          <ExemptionCoverage
            packageOption={packageOption}
            onChangeExemptionCoverage={onChangeExemptionCoverage}
            onChangeLoadingPackage={() => onChangeLoadingPackage?.()}
            currentSelectedPlan={currentSelectedPlan}
            unit={unit}
          />
        </Box>
      </Box>
      <PersonalizationModal
        data-testid="personilization-modal"
        open={openModal}
        coverages={customizableCoverages}
        anualPrice={anualPrice}
        packageOption={packageOption}
        moneyType={moneyType}
        onCloseRequested={handleCloseModal}
        onChangeCoverageLimit={onChangeCoverageLimit}
        currentSelectedPlan={currentSelectedPlan}
      />
    </>
  );
};

export default IndividualCoveragesCard;
